const BASE_URL = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}, { skipAuthRedirect = false } = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (res.status === 401 && !skipAuthRedirect) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const e = new Error((data && data.error) || `Request failed: ${res.status}`);
    e.status = res.status;
    throw e;
  }

  return data || {};
}

const api = {
  login: (email, password) =>
    request('/api/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  logout: () =>
    request('/api/logout', { method: 'POST' }),

  me: () =>
    request('/api/me', {}, { skipAuthRedirect: true }),

  getAccounts: () =>
    request('/api/accounts'),

  createAccount: (data) =>
    request('/api/accounts', { method: 'POST', body: JSON.stringify(data) }),

  disableAccount: (id) =>
    request(`/api/accounts/${id}/disable`, { method: 'PUT' }),

  getUsers: (accountId) =>
    request(`/api/accounts/${accountId}/users`),

  getDelegations: () =>
    request('/api/delegations'),

  createDelegation: (data) =>
    request('/api/delegations', { method: 'POST', body: JSON.stringify(data) }),

  deleteDelegation: (uuid) =>
    request(`/api/delegations/${uuid}`, { method: 'DELETE' }),

  getCompanies: () =>
    request('/api/companies'),

  getCompanyDetail: (id) =>
    request(`/api/companies/${id}`),

  getApplications: (companyId) =>
    request(`/api/companies/${companyId}/applications`),

  getTemplates: () =>
    request('/api/templates'),

  updateTemplate: (uuid, data) =>
    request(`/api/templates/${uuid}`, { method: 'PUT', body: JSON.stringify(data) }),

  refreshTemplateMeta: (uuid) =>
    request(`/api/templates/${uuid}/refresh-meta`, { method: 'POST' }),

  checkTemplateUpdates: () =>
    request('/api/templates/check-updates', { method: 'POST' }),

  syncAllTemplates: () =>
    request('/api/templates/sync-all', { method: 'POST' }),

  getPlacementTypes: () =>
    request('/api/placement-types'),

  createPlacementType: (data) =>
    request('/api/placement-types', { method: 'POST', body: JSON.stringify(data) }),

  updatePlacementType: (id, data) =>
    request(`/api/placement-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deletePlacementType: (id) =>
    request(`/api/placement-types/${id}`, { method: 'DELETE' }),

  getTemplateFieldConfig: (templateUUID) =>
    request(`/api/template-field-config/${templateUUID}`),

  saveTemplateFieldConfig: (templateUUID, fields, variants) =>
    request(`/api/template-field-config/${templateUUID}`, {
      method: 'PUT',
      body: JSON.stringify({ fields, variants }),
    }),

  getAccountFeatures: (accountUuid) =>
    request(`/api/account-features/${accountUuid}`),

  updateAccountFeatures: (accountUuid, data) =>
    request(`/api/account-features/${accountUuid}`, {
      method: 'PUT',
      body: JSON.stringify(typeof data === 'object' && data.features ? data : { features: data }),
    }),

  getAccountCompanies: (accountId) =>
    request(`/api/accounts/${accountId}/companies`),

  addAccountCompany: (accountId, data) =>
    request(`/api/accounts/${accountId}/companies`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeAccountCompany: (accountId, companyId) =>
    request(`/api/accounts/${accountId}/companies/${companyId}`, {
      method: 'DELETE',
    }),

  getAccountApplications: (accountUuid) =>
    request(`/api/accounts/${accountUuid}/applications`),

  updateApplicationIntegrationType: (accountUuid, appUuid, integrationType) =>
    request(`/api/accounts/${accountUuid}/applications/${appUuid}`, {
      method: 'PUT',
      body: JSON.stringify({ integration_type: integrationType }),
    }),

  syncAccountApplications: (accountUuid) =>
    request(`/api/accounts/${accountUuid}/applications/sync`, { method: 'POST' }),

  createApplication: (accountUuid, data) =>
    request(`/api/accounts/${accountUuid}/applications`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Admin placements (hardcode only)
  getApplicationPlacements: (appId) =>
    request(`/api/admin/applications/${encodeURIComponent(appId)}/placements`),

  createAdminPlacement: (data) =>
    request('/api/admin/placements', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAdminPlacement: (uuid, data) =>
    request(`/api/admin/placements/${encodeURIComponent(uuid)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changeApplicationStatus: (appUuid, status) =>
    request(`/api/admin/applications/${encodeURIComponent(appUuid)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  changePlacementStatus: (uuid, status) =>
    request(`/api/admin/placements/${encodeURIComponent(uuid)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
};

export default api;
